"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface DocumentUploadFormProps {
  onNext: (data: DocumentUploadData) => void;
  onBack: () => void;
}

export interface DocumentUploadData {
  registrationCertificate: File | null;
  insuranceCertificate: File | null;
  inspectionReport: File | null;
}

export default function DocumentUploadForm({ onNext, onBack }: DocumentUploadFormProps) {
  const [documents, setDocuments] = useState<DocumentUploadData>({
    registrationCertificate: null,
    insuranceCertificate: null,
    inspectionReport: null,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof DocumentUploadData, string>>>({});

  const handleFileChange = (field: keyof DocumentUploadData, file: File | null) => {
    if (file) {
      const validTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [field]: "Kun PDF, JPG og PNG-filer er tillatt",
        }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          [field]: "Filstørrelse må være mindre enn 10MB",
        }));
        return;
      }
    }

    setDocuments((prev) => ({ ...prev, [field]: file }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDrop = (e: React.DragEvent, field: keyof DocumentUploadData) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(field, file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeFile = (field: keyof DocumentUploadData) => {
    handleFileChange(field, null);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof DocumentUploadData, string>> = {};

    if (!documents.registrationCertificate) {
      newErrors.registrationCertificate = "Registreringsbevis er påkrevd";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext(documents);
    }
  };

  const renderUploadZone = (
    field: keyof DocumentUploadData,
    label: string,
    required: boolean = false
  ) => {
    const file = documents[field];
    const error = errors[field];

    return (
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          {label} {required && "*"}
        </label>
        
        {!file ? (
          <div
            onDrop={(e) => handleDrop(e, field)}
            onDragOver={handleDragOver}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
              error
                ? "border-red-600 bg-red-50" :"border-gray-300 hover:border-blue-800 bg-gray-50"
            }`}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-blue-800 rounded-lg flex items-center justify-center">
                <Icon name="CloudArrowUpIcon" className="text-white" size={32} />
              </div>
              <div>
                <p className="text-gray-900 font-semibold mb-1">
                  Slipp filen din her, eller{" "}
                  <label className="text-blue-800 cursor-pointer hover:underline">
                    bla gjennom
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(field, e.target.files?.[0] || null)}
                      className="hidden"
                    />
                  </label>
                </p>
                <p className="text-sm text-gray-500">
                  PDF, JPG eller PNG (maks 10MB)
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-2 border-gray-200 rounded-lg p-4 bg-green-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="DocumentTextIcon" className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(field)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
              >
                <Icon name="TrashIcon" size={20} />
              </button>
            </div>
          </div>
        )}
        
        {error && (
          <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {renderUploadZone("registrationCertificate", "Registreringsbevis", true)}
      {renderUploadZone("insuranceCertificate", "Forsikringsbevis")}
      {renderUploadZone("inspectionReport", "Inspeksjonsrapport")}

      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="InformationCircleIcon" className="text-blue-800" size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">Retningslinjer for dokumenter</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Sørg for at dokumentene er klare og leselige</li>
              <li>• All tekst må være lesbar uten zooming</li>
              <li>• Dokumenter må være gjeldende og ikke utløpt</li>
              <li>• Aksepterte formater: PDF, JPG, PNG (maks 10MB hver)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="px-8 py-3 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all flex items-center gap-2"
        >
          <Icon name="ArrowLeftIcon" size={18} />
          Tilbake
        </button>
        <button
          type="submit"
          className="px-8 py-3 bg-blue-800 text-white rounded-lg font-semibold hover:bg-blue-900 transition-all flex items-center gap-2"
        >
          Gjennomgang og innsending
          <Icon name="ArrowRightIcon" size={18} />
        </button>
      </div>
    </form>
  );
}